import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  Loader2, ArrowLeft, Clock, User, Building2, Shield, Calendar,
  CheckCircle2, Circle, AlertTriangle, Package, Wrench, Phone,
  FileText, Play, PauseCircle, UserCheck, Lock, Send,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// ==================== Types ====================

interface WorkflowStep {
  step: number;
  key: string;
  label: string;
  expectedStatus: string;
  completed: boolean;
  completedAt: string | null;
  completedBy: string | null;
  data: any;
}

interface WarrantyInfo {
  id?: string;
  status: string;
  product?: string;
  serialNumber?: string;
  startDate?: string;
  endDate?: string;
}

interface TicketData {
  id: string;
  number: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  category?: string;
  productType?: string;
  resolution?: string;
  photos: string[];
  scheduledAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  escalatedAt?: string;
  escalationLevel: number;
  slaDeadline?: string;
  createdAt: string;
  updatedAt: string;
  contact?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  } | null;
  account?: { id: string; name: string } | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  warranty?: any;
}

interface WorkflowData {
  currentStep: number;
  currentStepLabel: string;
  currentStepKey: string;
  nextStep: { step: number; key: string; label: string } | null;
  isComplete: boolean;
  isOnHold: boolean;
  steps: WorkflowStep[];
}

interface PageData {
  ticket: TicketData;
  workflow: WorkflowData;
  warrantyInfo: WarrantyInfo | null;
  materials: string[];
  assignedTechnician: { id: string; firstName: string; lastName: string } | null;
}

// ==================== Constants ====================

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  on_hold: 'bg-orange-100 text-orange-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700',
  escalated: 'bg-red-100 text-red-800',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-red-700',
};

const SLA_HOURS: Record<string, number> = {
  critical: 4,
  high: 8,
  medium: 24,
  low: 48,
};

const STEP_ICONS: Record<string, typeof Phone> = {
  complaint_received: Phone,
  collect_details: FileText,
  hold_if_incomplete: PauseCircle,
  warranty_check: Shield,
  create_ticket: FileText,
  assign_tech: User,
  material_issue: Package,
  execute: Wrench,
  resolve: CheckCircle2,
  demo_signature: UserCheck,
  close: Lock,
};

// ==================== Component ====================

export function ServiceTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [employees, setEmployees] = useState<{ id: string; firstName: string; lastName: string }[]>([]);

  // Step-specific form data
  const [warrantySerial, setWarrantySerial] = useState('');
  const [warrantyStatus, setWarrantyStatus] = useState<'active' | 'expired' | 'not_found'>('active');
  const [technicianId, setTechnicianId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [materials, setMaterials] = useState('');
  const [resolution, setResolution] = useState('');
  const [closureMethod, setClosureMethod] = useState<'otp' | 'auto'>('otp');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [holdReason, setHoldReason] = useState('');

  // Lightweight inline toast (no external lib — AURA does not ship one yet)
  const [toastMsg, setToastMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const showToast = useCallback((kind: 'success' | 'error', text: string) => {
    setToastMsg({ kind, text });
    window.setTimeout(() => setToastMsg((prev) => (prev && prev.text === text ? null : prev)), 3000);
  }, []);
  const toast = {
    success: (text: string) => showToast('success', text),
    error: (text: string) => showToast('error', text),
  };

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/service-tickets/${id}/workflow`);
      setData(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchEmployees = useCallback(async () => {
    try {
      // Fetch users (not employees) — assignedToId is a User FK
      const res = await api.get('/auth/users?limit=100');
      const users = res.data.data?.users || res.data.data || [];
      setEmployees(users.map((u: any) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName })));
    } catch {
      // Fallback to employees if users endpoint not available
      try {
        const res = await api.get('/employees?limit=100');
        setEmployees(res.data.data || []);
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchEmployees();
  }, [fetchData, fetchEmployees]);

  const advanceStep = async (stepKey: string, stepData: any = {}) => {
    if (!id) return;
    try {
      setActionLoading(true);
      await api.post(`/service-tickets/${id}/workflow-step`, {
        step: stepKey,
        data: stepData,
      });
      await fetchData();
      toast.success(`Step "${stepKey.replace(/_/g, ' ')}" completed`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to advance workflow');
    } finally {
      setActionLoading(false);
    }
  };

  const resumeFromHold = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await api.post(`/service-tickets/${id}/workflow-resume`);
      await fetchData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to resume');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading ticket...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-sm text-red-500">{error || 'Ticket not found'}</p>
        <button onClick={() => navigate('/service-tickets')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">
          Back to Tickets
        </button>
      </div>
    );
  }

  const { ticket, workflow, warrantyInfo, materials: ticketMaterials, assignedTechnician } = data;

  // SLA calculation
  const slaMs = (SLA_HOURS[ticket.priority] ?? 24) * 60 * 60 * 1000;
  const elapsedMs = Date.now() - new Date(ticket.createdAt).getTime();
  const remainingMs = slaMs - elapsedMs;
  const isTerminal = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <div>
      {/* Toast */}
      {toastMsg && (
        <div
          className={`fixed top-5 right-5 z-50 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            toastMsg.kind === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toastMsg.text}
        </div>
      )}
      {/* Back + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/service-tickets')}
          className="mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={16} />
          Back to Tickets
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-gray-400">{ticket.number}</span>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[ticket.status || 'open'] || STATUS_COLORS.open}`}>
                {(ticket.status || 'open').replace('_', ' ').toUpperCase()}
              </span>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${PRIORITY_COLORS[ticket.priority || 'medium'] || PRIORITY_COLORS.medium}`}>
                {ticket.priority ? ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) : 'Medium'}
              </span>
              {ticket.escalationLevel > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                  Escalation L{ticket.escalationLevel}
                </span>
              )}
            </div>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">{ticket.title}</h1>
          </div>
          {/* SLA Timer */}
          {!isTerminal && (
            <div className={`flex items-center gap-2 rounded-lg border px-4 py-2 ${remainingMs <= 0 ? 'border-red-300 bg-red-50' : remainingMs < slaMs * 0.25 ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'}`}>
              <Clock size={16} className={remainingMs <= 0 ? 'text-red-600' : 'text-gray-500'} />
              <div className="text-xs">
                <p className="font-medium text-gray-700">SLA: {SLA_HOURS[ticket.priority] ?? 24}h</p>
                <p className={`font-bold ${remainingMs <= 0 ? 'text-red-700' : 'text-gray-900'}`}>
                  {remainingMs <= 0 ? 'BREACHED' : `${Math.floor(remainingMs / 3600000)}h ${Math.floor((remainingMs % 3600000) / 60000)}m remaining`}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Workflow Progress Bar */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-gray-700">Service Workflow Progress</h2>
            <div className="flex items-center gap-1 overflow-x-auto pb-2">
              {workflow.steps.map((step, idx) => {
                const StepIcon = STEP_ICONS[step.key] || Circle;
                const isCurrent = step.step === workflow.currentStep;
                const isCompleted = step.completed;
                const isFuture = step.step > workflow.currentStep;

                return (
                  <div key={step.key} className="flex items-center">
                    <div className="flex flex-col items-center" title={`${step.label}${isCompleted ? ' (Done)' : isCurrent ? ' (Current)' : ''}`}>
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors ${
                          isCompleted
                            ? 'border-green-500 bg-green-500 text-white'
                            : isCurrent
                              ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]'
                              : 'border-gray-300 bg-white text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle2 size={16} />
                        ) : (
                          <StepIcon size={14} />
                        )}
                      </div>
                      <span className={`mt-1 text-center text-[9px] leading-tight w-14 ${
                        isCompleted ? 'text-green-700 font-medium' :
                        isCurrent ? 'text-[var(--ink-0)] font-semibold' :
                        'text-gray-400'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                    {idx < workflow.steps.length - 1 && (
                      <div className={`mx-0.5 h-0.5 w-4 flex-shrink-0 ${
                        isCompleted ? 'bg-green-400' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current Step Action Panel */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Play size={16} className="text-[var(--ink-0)]" />
              Current Step: {workflow.currentStepLabel || 'Complete'}
              {workflow.isOnHold && (
                <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">ON HOLD</span>
              )}
            </h2>

            {workflow.isComplete ? (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-green-700">
                <CheckCircle2 size={20} />
                <span className="text-sm font-medium">Workflow complete. Ticket is closed.</span>
              </div>
            ) : workflow.isOnHold ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-orange-50 p-4 text-sm text-orange-700">
                  <p className="font-medium">Ticket is on hold due to incomplete information.</p>
                  <p className="text-xs mt-1">Once the missing details are collected, resume the workflow.</p>
                </div>
                <button
                  onClick={resumeFromHold}
                  disabled={actionLoading}
                  className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                >
                  {actionLoading && <Loader2 size={14} className="animate-spin" />}
                  Resume Workflow
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Step 1-2: Info display */}
                {(workflow.currentStep === 1 || workflow.currentStep === 2) && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      {workflow.currentStep === 1
                        ? 'Complaint has been received. Verify details and proceed.'
                        : 'Collect product type, serial number, description, and photos.'}
                    </p>
                    {ticket.description && (
                      <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{ticket.description}</div>
                    )}
                    <button
                      onClick={() => advanceStep(workflow.currentStepKey)}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      {workflow.currentStep === 1 ? 'Acknowledge & Proceed' : 'Details Collected'}
                    </button>
                  </div>
                )}

                {/* Step 3: Hold if Incomplete */}
                {workflow.currentStep === 3 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Mark as on hold if information is missing, or skip to warranty verification.</p>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Hold Reason (optional)</label>
                      <input
                        type="text"
                        value={holdReason}
                        onChange={(e) => setHoldReason(e.target.value)}
                        placeholder="e.g., Missing serial number"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => advanceStep('hold_if_incomplete', { holdReason })}
                        disabled={actionLoading}
                        className="flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
                      >
                        {actionLoading && <Loader2 size={14} className="animate-spin" />}
                        Mark as On Hold
                      </button>
                      <button
                        onClick={() => advanceStep('hold_if_incomplete', {})}
                        disabled={actionLoading}
                        className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                      >
                        Skip (Info Complete)
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Warranty Verification */}
                {workflow.currentStep === 4 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Check warranty status by serial number.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Serial Number</label>
                        <input
                          type="text"
                          value={warrantySerial}
                          onChange={(e) => setWarrantySerial(e.target.value)}
                          placeholder="Enter serial number"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Warranty Status</label>
                        <select
                          value={warrantyStatus}
                          onChange={(e) => setWarrantyStatus(e.target.value as any)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        >
                          <option value="active">Active</option>
                          <option value="expired">Expired</option>
                          <option value="not_found">Not Found</option>
                        </select>
                      </div>
                    </div>
                    <button
                      onClick={() => advanceStep('warranty_check', { serialNumber: warrantySerial, warrantyStatus })}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      Confirm Warranty Status
                    </button>
                  </div>
                )}

                {/* Step 5: Create Ticket (already done) */}
                {workflow.currentStep === 5 && (
                  <div className="space-y-3">
                    <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
                      Ticket {ticket.number} has already been created. Proceeding to pre-visit planning.
                    </div>
                    <button
                      onClick={() => advanceStep('create_ticket')}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      Proceed to Planning
                    </button>
                  </div>
                )}

                {/* Step 6: Pre-visit Planning */}
                {workflow.currentStep === 6 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Assign a technician and schedule the visit.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Assign Technician</label>
                        <select
                          value={technicianId}
                          onChange={(e) => setTechnicianId(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        >
                          <option value="">Select technician...</option>
                          {employees.map((e) => (
                            <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Schedule Date</label>
                        <input
                          type="datetime-local"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => advanceStep('assign_tech', { technicianId, scheduledDate })}
                      disabled={actionLoading || !technicianId}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      Assign & Schedule
                    </button>
                  </div>
                )}

                {/* Step 7: Material Issue */}
                {workflow.currentStep === 7 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">List any parts or materials needed for this service.</p>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Materials (comma-separated)</label>
                      <textarea
                        value={materials}
                        onChange={(e) => setMaterials(e.target.value)}
                        placeholder="e.g., Replacement gasket, Sealant, Wrench set"
                        rows={3}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                      />
                    </div>
                    <button
                      onClick={() => advanceStep('material_issue', {
                        materialsNeeded: materials.split(',').map(m => m.trim()).filter(Boolean),
                      })}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      Log Materials & Proceed
                    </button>
                  </div>
                )}

                {/* Step 8: Service Execution */}
                {workflow.currentStep === 8 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Technician is performing the service. Mark as in progress.</p>
                    <button
                      onClick={() => advanceStep('execute', { executionNotes: 'Service execution started' })}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      Mark as In Progress
                    </button>
                  </div>
                )}

                {/* Step 9: Resolved? */}
                {workflow.currentStep === 9 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Was the issue resolved? If not, reassign or escalate.</p>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Resolution Description</label>
                      <textarea
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder="Describe what was done..."
                        rows={3}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => advanceStep('resolve', { resolved: true, resolution })}
                        disabled={actionLoading || !resolution}
                        className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {actionLoading && <Loader2 size={14} className="animate-spin" />}
                        Resolved
                      </button>
                      <button
                        onClick={() => advanceStep('resolve', { resolved: false, resolution: resolution || 'Not resolved' })}
                        disabled={actionLoading}
                        className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                      >
                        Not Resolved (Escalate)
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 10: Demo + Signature */}
                {workflow.currentStep === 10 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">Customer confirms the fix. Get signature/confirmation.</p>
                    <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
                      <input
                        type="checkbox"
                        id="customerConfirm"
                        className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                      />
                      <label htmlFor="customerConfirm" className="text-sm text-gray-700">Customer has confirmed the fix</label>
                    </div>
                    <button
                      onClick={() => advanceStep('demo_signature', { customerSignature: true, demoCompleted: true })}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      Confirm & Proceed to Close
                    </button>
                  </div>
                )}

                {/* Step 11: Closure */}
                {workflow.currentStep === 11 && !workflow.isComplete && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">Close the ticket. Choose closure method.</p>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Closure Method</label>
                      <select
                        value={closureMethod}
                        onChange={(e) => {
                          setClosureMethod(e.target.value as any);
                          setOtpSent(false); // reset OTP flow on switch
                        }}
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                      >
                        <option value="otp">Verify OTP from Customer</option>
                        <option value="auto">Auto-close after 24hrs</option>
                      </select>
                    </div>

                    {closureMethod === 'otp' && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        {!otpSent ? (
                          <div className="flex flex-col gap-3">
                            <p className="text-sm text-blue-700">Send an OTP to the customer's registered mobile number.</p>
                            <button
                              onClick={() => setOtpSent(true)}
                              className="self-start rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                            >
                              Send OTP
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            <p className="text-sm text-blue-700">OTP sent! Enter the code provided by the customer.</p>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Enter OTP"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value)}
                                className="w-32 rounded-lg border border-[var(--border-primary)] px-3 py-2 text-sm"
                              />
                            </div>
                            <button
                              onClick={() => setOtpSent(false)}
                              className="text-xs font-medium text-blue-600 hover:underline self-start"
                            >
                              Resend OTP
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {closureMethod === 'auto' && (
                      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                        <div className="flex gap-2">
                          <AlertTriangle size={18} className="text-orange-600 shrink-0" />
                          <div className="text-sm text-orange-800">
                            <strong>24h Auto-Close Window</strong>
                            <p className="mt-1">
                              The customer will receive an SMS notifying them that the issue is marked resolved. 
                              The ticket will automatically close in 24 hours if the customer does not reopen it.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => advanceStep('close', { closureMethod, otp: otpCode })}
                      disabled={actionLoading || (closureMethod === 'otp' && (!otpSent || otpCode.length < 4))}
                      className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {actionLoading && <Loader2 size={14} className="animate-spin" />}
                      {closureMethod === 'otp' ? 'Verify OTP & Close' : 'Schedule Auto-Close'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-gray-700">Workflow Timeline</h2>
            {workflow.steps.filter(s => s.completed).length === 0 ? (
              <p className="text-xs text-gray-400">No steps completed yet.</p>
            ) : (
              <div className="space-y-3">
                {workflow.steps.filter(s => s.completed).map((step) => (
                  <div key={step.key} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                        <CheckCircle2 size={14} className="text-green-600" />
                      </div>
                      <div className="h-full w-px bg-gray-200" />
                    </div>
                    <div className="flex-1 pb-3">
                      <p className="text-sm font-medium text-gray-800">
                        Step {step.step}: {step.label}
                      </p>
                      {step.completedAt && (
                        <p className="text-xs text-gray-400">{formatDate(step.completedAt)} at {new Date(step.completedAt).toLocaleTimeString()}</p>
                      )}
                      {step.data && Object.keys(step.data).length > 0 && (
                        <div className="mt-1 rounded bg-gray-50 p-2 text-xs text-gray-600">
                          {step.data.warrantyStatus && <p>Warranty: {step.data.warrantyStatus}</p>}
                          {step.data.serialNumber && <p>S/N: {step.data.serialNumber}</p>}
                          {step.data.materialsNeeded?.length > 0 && (
                            <p>Materials: {step.data.materialsNeeded.join(', ')}</p>
                          )}
                          {step.data.resolution && <p>Resolution: {step.data.resolution}</p>}
                          {step.data.closureMethod && <p>Closure: {step.data.closureMethod}</p>}
                          {step.data.holdReason && <p>Hold reason: {step.data.holdReason}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-6">
          {/* Ticket Details */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-3 text-sm font-semibold text-gray-700">Ticket Details</h3>
            <dl className="space-y-2.5 text-sm">
              {ticket.description && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Description</dt>
                  <dd className="text-gray-700">{ticket.description}</dd>
                </div>
              )}
              {ticket.category && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Category</dt>
                  <dd className="text-gray-700 capitalize">{ticket.category}</dd>
                </div>
              )}
              {ticket.productType && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Product Type</dt>
                  <dd className="text-gray-700">{ticket.productType}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-gray-400">Created</dt>
                <dd className="text-gray-700">{formatDate(ticket.createdAt)}</dd>
              </div>
              {ticket.scheduledAt && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Scheduled</dt>
                  <dd className="text-gray-700">{formatDate(ticket.scheduledAt)}</dd>
                </div>
              )}
              {ticket.resolvedAt && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Resolved</dt>
                  <dd className="text-gray-700">{formatDate(ticket.resolvedAt)}</dd>
                </div>
              )}
              {ticket.closedAt && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Closed</dt>
                  <dd className="text-gray-700">{formatDate(ticket.closedAt)}</dd>
                </div>
              )}
              {ticket.resolution && (
                <div>
                  <dt className="text-xs font-medium text-gray-400">Resolution</dt>
                  <dd className="text-gray-700">{ticket.resolution}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Customer Info */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <User size={14} />
              Customer
            </h3>
            {ticket.contact ? (
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs font-medium text-gray-400">Name</dt>
                  <dd className="text-gray-700">{joinPersonName(ticket.contact.firstName, ticket.contact.lastName)}</dd>
                </div>
                {ticket.contact.email && (
                  <div>
                    <dt className="text-xs font-medium text-gray-400">Email</dt>
                    <dd className="text-gray-700">{ticket.contact.email}</dd>
                  </div>
                )}
                {ticket.contact.phone && (
                  <div>
                    <dt className="text-xs font-medium text-gray-400">Phone</dt>
                    <dd className="text-gray-700">{ticket.contact.phone}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-xs text-gray-400">No customer linked</p>
            )}
            {ticket.account && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                <Building2 size={14} className="text-gray-400" />
                <span className="text-sm text-gray-700">{ticket.account.name}</span>
              </div>
            )}
          </div>

          {/* Warranty Info */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Shield size={14} />
              Warranty
            </h3>
            {warrantyInfo ? (
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs font-medium text-gray-400">Status</dt>
                  <dd>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      warrantyInfo.status === 'active' ? 'bg-green-100 text-green-700' :
                      warrantyInfo.status === 'expired' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {warrantyInfo.status ? warrantyInfo.status.charAt(0).toUpperCase() + warrantyInfo.status.slice(1) : 'Unknown'}
                    </span>
                  </dd>
                </div>
                {warrantyInfo.product && (
                  <div>
                    <dt className="text-xs font-medium text-gray-400">Product</dt>
                    <dd className="text-gray-700">{warrantyInfo.product}</dd>
                  </div>
                )}
                {warrantyInfo.serialNumber && (
                  <div>
                    <dt className="text-xs font-medium text-gray-400">Serial Number</dt>
                    <dd className="font-mono text-gray-700">{warrantyInfo.serialNumber}</dd>
                  </div>
                )}
                {warrantyInfo.endDate && (
                  <div>
                    <dt className="text-xs font-medium text-gray-400">Expires</dt>
                    <dd className="text-gray-700">{formatDate(warrantyInfo.endDate)}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-xs text-gray-400">Not checked yet</p>
            )}
          </div>

          {/* Assigned Technician */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Wrench size={14} />
              Assigned Technician
            </h3>
            {assignedTechnician ? (
              <p className="text-sm text-gray-700">{assignedTechnician.firstName} {assignedTechnician.lastName}</p>
            ) : (
              <p className="text-xs text-gray-400">Not assigned yet</p>
            )}
          </div>

          {/* Materials */}
          {ticketMaterials.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Package size={14} />
                Materials Needed
              </h3>
              <ul className="space-y-1">
                {ticketMaterials.map((mat, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                    {mat}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
